/**
 * Chat Notification Context (Admin)
 * Manages unread per-order counts and toast notifications for admins.
 * Tracks { [orderId]: count } for per-button badges in order tables.
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { getChatSocket } from '../services/chatSocket';
import chatApi from '../services/chatApi';
import toast from 'react-hot-toast';
import { useChatAuth } from './ChatAuthContext';
import { playNotificationSound, startTabAlert, stopTabAlert } from '../utils/notificationUtils';

const ChatNotificationContext = createContext(null);

export const useChatNotification = () => {
    const context = useContext(ChatNotificationContext);
    if (!context) {
        throw new Error('useChatNotification must be used within ChatNotificationProvider');
    }
    return context;
};

export const ChatNotificationProvider = ({ children }) => {
    // Per-order unread counts: { [orderId]: number }
    const [unreadCounts, setUnreadCounts] = useState({});
    const { isAuthenticated } = useChatAuth();

    // Track which order chat admin currently has open (suppress notifications for it)
    const activeOrderIdRef = useRef(null);

    const setActiveOrderId = useCallback((orderId) => {
        activeOrderIdRef.current = orderId;
    }, []);

    /** Clear unread count for a specific order (called when admin opens that chat) */
    const markOrderRead = useCallback((orderId) => {
        setUnreadCounts((prev) => {
            if (!prev[orderId]) return prev;
            const next = { ...prev };
            delete next[orderId];
            return next;
        });
    }, []);

    /** Get unread count for a specific order */
    const getOrderUnreadCount = useCallback(
        (orderId) => unreadCounts[orderId] || 0,
        [unreadCounts]
    );

    /** Total across all orders (kept for backwards compat) */
    const totalUnreadCount = Object.values(unreadCounts).reduce((a, b) => a + b, 0);

    const fetchUnreadBatchCounts = useCallback(async () => {
        if (!isAuthenticated) return;
        try {
            const response = await chatApi.get('/messages/unread-counts/batch');
            // Assuming response.data.counts is { [orderId]: sum }
            if (response.data && response.data.counts) {
                setUnreadCounts(response.data.counts);
            }
        } catch (error) {
            console.error('Error fetching admin unread batch counts:', error);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated) {
            fetchUnreadBatchCounts();

            const socket = getChatSocket();
            if (socket) {
                const handleNotification = (data) => {
                    // Suppress if admin is already viewing that exact chat
                    if (activeOrderIdRef.current && data.orderId === activeOrderIdRef.current) {
                        return;
                    }

                    // Increment per-order unread count
                    setUnreadCounts((prev) => ({
                        ...prev,
                        [data.orderId]: (prev[data.orderId] || 0) + 1,
                    }));

                    // Play sound and flash tab title
                    playNotificationSound();
                    if (document.visibilityState !== 'visible') {
                        startTabAlert('(1) New Message - DoubtQ Admin');
                    }

                    // Show toast notification
                    toast((t) => (
                        <div
                            onClick={() => toast.dismiss(t.id)}
                            className="chat-toast-notification"
                            style={{ cursor: 'pointer', width: '100%' }}
                        >
                            <p style={{ fontWeight: 'bold', margin: 0 }}>Order {data.orderId}</p>
                            <p style={{ fontSize: '0.875rem', margin: 0 }}>{data.body || 'New message received'}</p>
                        </div>
                    ), {
                        duration: 5000,
                        position: 'top-right',
                        style: { cursor: 'pointer' },
                    });
                };

                socket.on('new-notification', handleNotification);

                const handleFocus = () => stopTabAlert();
                window.addEventListener('focus', handleFocus);

                return () => {
                    socket.off('new-notification', handleNotification);
                    window.removeEventListener('focus', handleFocus);
                    stopTabAlert();
                };
            }
        }
    }, [isAuthenticated]);

    const value = {
        unreadCounts,
        totalUnreadCount,
        getOrderUnreadCount,
        markOrderRead,
        setActiveOrderId,
    };

    return (
        <ChatNotificationContext.Provider value={value}>
            {children}
        </ChatNotificationContext.Provider>
    );
};

export default ChatNotificationContext;
